import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileText, Upload, CheckCircle, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CountryCodeSelector from '../components/CountryCodeSelector';
import { formatPhoneNumber, formatPhoneForCRM, isValidPhoneNumber } from '../utils/phoneFormatter';
import { submitToBlackOps, captureAttribution } from '../utils/leadCapture';
import TurnstileWidget from '../components/TurnstileWidget';
import { useTurnstile } from '../hooks/useTurnstile';
import { TURNSTILE_REQUIRED_MESSAGE } from '../utils/turnstile';
import SEOHead from '../components/SEOHead';

const CROPS = [
  'Almonds',
  'Apples',
  'Apricots',
  'Asparagus',
  'Blueberries',
  'Cannabis',
  'Cherries',
  'Grapes',
  'Hops',
  'Pistachios',
  'Raspberries',
  'Tomatoes',
  'Multiple/Not sure',
  'Other'
];

const COUNTRIES = [
  'United States',
  'Canada',
  'Mexico',
  'Australia',
  'New Zealand',
  'United Kingdom',
  'France',
  'Spain',
  'Italy',
  'Greece',
  'Morocco',
  'Saudi Arabia',
  'United Arab Emirates',
  'Israel',
  'South Africa',
  'Chile',
  'Argentina',
  'Other'
];

const ChemistryReviewPage: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    city: '',
    state: '',
    country: '',
    crop: '',
    peakFlowGpm: '',
    currentPh: '',
    alkalinity: '',
    notes: ''
  });

  const [objectives, setObjectives] = useState({
    hitPhSetpoint: false,
    reduceScaling: false,
    improveInjector: false,
    other: false,
    otherText: ''
  });

  const [countryCode, setCountryCode] = useState('+1');
  const [uploadedFiles, setUploadedFiles] = useState<{
    waterReport: File | null;
    soilReport: File | null;
    photos: File[];
  }>({
    waterReport: null,
    soilReport: null,
    photos: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const turnstile = useTurnstile();
  const [submittedData, setSubmittedData] = useState<any>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleObjectiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked, value } = e.target;
    if (name === 'otherText') {
      setObjectives(prev => ({ ...prev, otherText: value }));
    } else {
      setObjectives(prev => ({ ...prev, [name]: checked }));
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'waterReport' | 'soilReport' | 'photos') => {
    if (type === 'photos') {
      if (e.target.files) {
        const newPhotos = Array.from(e.target.files);
        setUploadedFiles(prev => ({
          ...prev,
          photos: [...prev.photos, ...newPhotos]
        }));
      }
    } else {
      if (e.target.files && e.target.files[0]) {
        setUploadedFiles(prev => ({
          ...prev,
          [type]: e.target.files![0]
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!isValidPhoneNumber(formData.phone)) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!formData.city || !formData.state) {
      setError('Please enter your city and state');
      return;
    }

    const peakFlowNum = parseFloat(formData.peakFlowGpm);
    if (isNaN(peakFlowNum) || peakFlowNum <= 0) {
      setError('Please enter a valid peak flow (GPM)');
      return;
    }

    const phNum = parseFloat(formData.currentPh);
    if (isNaN(phNum) || phNum < 0 || phNum > 14) {
      setError('Please enter a valid pH (0-14)');
      return;
    }

    if (!turnstile.token) {
      setError(TURNSTILE_REQUIRED_MESSAGE);
      return;
    }

    setIsSubmitting(true);

    try {
      const formattedPhone = formatPhoneForCRM(countryCode, formData.phone);

      const objectivesData: string[] = [];
      if (objectives.hitPhSetpoint) objectivesData.push('Hit/hold pH setpoint');
      if (objectives.reduceScaling) objectivesData.push('Reduce scaling/clogging risk');
      if (objectives.improveInjector) objectivesData.push('Improve injector performance / distribution uniformity');
      if (objectives.other && objectives.otherText) objectivesData.push(`Other: ${objectives.otherText}`);

      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const submissionData = {
        full_name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formattedPhone,
        company: formData.company.trim() || null,
        city: formData.city.trim(),
        state: formData.state.trim(),
        country: formData.country,
        crop: formData.crop || null,
        peak_flow_gpm: parseFloat(formData.peakFlowGpm),
        current_ph: parseFloat(formData.currentPh),
        alkalinity: formData.alkalinity ? parseFloat(formData.alkalinity) : null,
        objectives: objectivesData,
        notes: formData.notes.trim() || null,
      };

      const result = await submitToBlackOps({
        form_type: 'quote_request',
        form_id: 'chemistry_review',
        first_name: firstName,
        last_name: lastName,
        email: formData.email.trim(),
        phone: formattedPhone || undefined,
        company_name: formData.company.trim() || undefined,
        product_interest: 'Sulfur Burners',
        message: formData.notes.trim() || undefined,
        extra_fields: {
          city: formData.city.trim() || null,
          state_region: formData.state.trim() || null,
          country: formData.country || null,
          crop: formData.crop || null,
          peak_flow_gpm: formData.peakFlowGpm ? Number(formData.peakFlowGpm) : null,
          current_ph: formData.currentPh ? Number(formData.currentPh) : null,
          alkalinity: formData.alkalinity ? Number(formData.alkalinity) : null,
          objectives: objectivesData.length > 0 ? objectivesData : null,
        },
        turnstile_token: turnstile.token,
        ...captureAttribution(),
      });

      if (result.success) {
        setSubmittedData({...submissionData, objectivesList: objectivesData});
        setIsSuccess(true);
      } else {
        console.error('Lead capture error:', result.error);
        setError('Unable to submit your request. Please try again or call us at (509) 901-7476.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError('An unexpected error occurred. Please try again or call us at (509) 901-7476.');
    } finally {
      // Turnstile tokens are single-use — clear it so a second submission works.
      turnstile.reset();
      setIsSubmitting(false);
    }
  };

  if (isSuccess && submittedData) {
    const hasUploads = uploadedFiles.waterReport || uploadedFiles.soilReport || uploadedFiles.photos.length > 0;

    return (
      <>
        <SEOHead
          title="Chemistry Review Request Received - CTC Waterworks"
          description="Your chemistry review request has been received. We'll send you a detailed action plan soon."
        />
        <div className="min-h-screen flex flex-col bg-gray-50">
          <Navbar />
          <main className="flex-grow py-12 px-4">
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
                <div className="text-center mb-8">
                  <CheckCircle className="mx-auto mb-4 text-green-600" size={64} />
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">
                    You're in.
                  </h1>
                  <p className="text-lg text-gray-700 mb-4">
                    We'll send a concise action plan covering target setpoints, recommended burner approach, commissioning checkpoints, and operating guardrails.
                  </p>
                  {!hasUploads && (
                    <p className="text-base text-gray-600 italic">
                      No uploads? No problem—our first pass starts with pH + GPM and recommended testing.
                    </p>
                  )}
                </div>

                <div className="bg-blue-50 rounded-lg p-6 mb-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">What we received:</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-semibold text-gray-700">Contact:</p>
                      <p className="text-gray-600">{submittedData.full_name}</p>
                      <p className="text-gray-600">{submittedData.email}</p>
                      <p className="text-gray-600">{submittedData.phone}</p>
                      {submittedData.company && <p className="text-gray-600">{submittedData.company}</p>}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700">Location:</p>
                      <p className="text-gray-600">{submittedData.city}, {submittedData.state}</p>
                      {submittedData.crop && (
                        <>
                          <p className="font-semibold text-gray-700 mt-2">Crop:</p>
                          <p className="text-gray-600">{submittedData.crop}</p>
                        </>
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700">Peak Flow:</p>
                      <p className="text-gray-600">{submittedData.peak_flow_gpm} GPM</p>
                      <p className="font-semibold text-gray-700 mt-2">Current pH:</p>
                      <p className="text-gray-600">{submittedData.current_ph}</p>
                    </div>
                    <div>
                      {submittedData.alkalinity && (
                        <>
                          <p className="font-semibold text-gray-700">Alkalinity:</p>
                          <p className="text-gray-600">{submittedData.alkalinity}</p>
                        </>
                      )}
                    </div>
                  </div>
                  {submittedData.objectivesList && submittedData.objectivesList.length > 0 && (
                    <div className="mt-4">
                      <p className="font-semibold text-gray-700">Objectives:</p>
                      <ul className="list-disc list-inside text-gray-600">
                        {submittedData.objectivesList.map((obj: string, idx: number) => (
                          <li key={idx}>{obj}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {submittedData.notes && (
                    <div className="mt-4">
                      <p className="font-semibold text-gray-700">Notes:</p>
                      <p className="text-gray-600">{submittedData.notes}</p>
                    </div>
                  )}
                  {hasUploads && (
                    <div className="mt-4">
                      <p className="font-semibold text-gray-700">Uploads:</p>
                      <ul className="list-disc list-inside text-gray-600">
                        {uploadedFiles.waterReport && <li>Water Report: {uploadedFiles.waterReport.name}</li>}
                        {uploadedFiles.soilReport && <li>Soil Report: {uploadedFiles.soilReport.name}</li>}
                        {uploadedFiles.photos.length > 0 && <li>{uploadedFiles.photos.length} photo(s)</li>}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/instant-quote"
                    className="flex-1 bg-white text-ctc-blue border-2 border-ctc-blue px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    Also request a quote
                    <ArrowRight size={20} />
                  </Link>
                  <Link
                    to="/sulfur-burners"
                    className="flex-1 bg-ctc-blue text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    Back to Sulfur Burners
                  </Link>
                </div>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Free Chemistry Review + Action Plan - Sulfur Burners - CTC Waterworks"
        description="Get a free irrigation water chemistry review and action plan. Upload your reports or start with just pH + GPM."
      />
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <main className="flex-grow py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <FileText className="mx-auto mb-4 text-ctc-blue" size={48} />
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  Free Chemistry Review + Action Plan
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Uploads optional. We'll start with pH + GPM and tell you what to test next.
                </p>
              </div>

              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <div className="text-red-600 text-sm">{error}</div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <div className="flex">
                      <CountryCodeSelector value={countryCode} onChange={setCountryCode} />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="flex-1 px-4 py-3 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                        placeholder="555 123 4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Farm
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                      placeholder="Your farm or company name"
                    />
                  </div>

                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                      placeholder="Yakima"
                    />
                  </div>

                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                      State/Province/Region *
                    </label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                      placeholder="WA"
                    />
                  </div>

                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                      Country *
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                    >
                      <option value="">Select country</option>
                      {COUNTRIES.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="crop" className="block text-sm font-medium text-gray-700 mb-2">
                      Crop
                    </label>
                    <select
                      id="crop"
                      name="crop"
                      value={formData.crop}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                    >
                      <option value="">Select a crop (optional)</option>
                      {CROPS.map(crop => (
                        <option key={crop} value={crop}>{crop}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="peakFlowGpm" className="block text-sm font-medium text-gray-700 mb-2">
                      Peak Flow (GPM) *
                    </label>
                    <input
                      type="number"
                      id="peakFlowGpm"
                      name="peakFlowGpm"
                      value={formData.peakFlowGpm}
                      onChange={handleChange}
                      required
                      step="0.1"
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                      placeholder="100"
                    />
                  </div>

                  <div>
                    <label htmlFor="currentPh" className="block text-sm font-medium text-gray-700 mb-2">
                      Current Irrigation Water pH *
                    </label>
                    <input
                      type="number"
                      id="currentPh"
                      name="currentPh"
                      value={formData.currentPh}
                      onChange={handleChange}
                      required
                      step="0.1"
                      min="0"
                      max="14"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                      placeholder="7.8"
                    />
                  </div>

                  <div>
                    <label htmlFor="alkalinity" className="block text-sm font-medium text-gray-700 mb-2">
                      Water Alkalinity/Bicarbonates
                    </label>
                    <input
                      type="number"
                      id="alkalinity"
                      name="alkalinity"
                      value={formData.alkalinity}
                      onChange={handleChange}
                      step="0.1"
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                      placeholder="Optional (ppm or mg/L)"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Optional. If you don't have it, submit anyway—start with pH + GPM.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Objectives (check all that apply)
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hitPhSetpoint"
                        checked={objectives.hitPhSetpoint}
                        onChange={handleObjectiveChange}
                        className="h-4 w-4 text-ctc-blue focus:ring-ctc-blue border-gray-300 rounded"
                      />
                      <span className="ml-2 text-gray-700">Hit/hold pH setpoint</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="reduceScaling"
                        checked={objectives.reduceScaling}
                        onChange={handleObjectiveChange}
                        className="h-4 w-4 text-ctc-blue focus:ring-ctc-blue border-gray-300 rounded"
                      />
                      <span className="ml-2 text-gray-700">Reduce scaling/clogging risk</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="improveInjector"
                        checked={objectives.improveInjector}
                        onChange={handleObjectiveChange}
                        className="h-4 w-4 text-ctc-blue focus:ring-ctc-blue border-gray-300 rounded"
                      />
                      <span className="ml-2 text-gray-700">Improve injector performance / distribution uniformity</span>
                    </label>
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        name="other"
                        checked={objectives.other}
                        onChange={handleObjectiveChange}
                        className="h-4 w-4 mt-1 text-ctc-blue focus:ring-ctc-blue border-gray-300 rounded"
                      />
                      <div className="ml-2 flex-1">
                        <span className="text-gray-700">Other</span>
                        {objectives.other && (
                          <input
                            type="text"
                            name="otherText"
                            value={objectives.otherText}
                            onChange={handleObjectiveChange}
                            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                            placeholder="Describe your objective..."
                          />
                        )}
                      </div>
                    </label>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Uploads (Optional)
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="waterReport" className="block text-sm font-medium text-gray-700 mb-2">
                        Water Report
                      </label>
                      <input
                        type="file"
                        id="waterReport"
                        onChange={(e) => handleFileUpload(e, 'waterReport')}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-ctc-blue hover:file:bg-blue-100"
                      />
                    </div>

                    <div>
                      <label htmlFor="soilReport" className="block text-sm font-medium text-gray-700 mb-2">
                        Soil Report
                      </label>
                      <input
                        type="file"
                        id="soilReport"
                        onChange={(e) => handleFileUpload(e, 'soilReport')}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-ctc-blue hover:file:bg-blue-100"
                      />
                    </div>

                    <div>
                      <label htmlFor="photos" className="block text-sm font-medium text-gray-700 mb-2">
                        System Photos
                      </label>
                      <input
                        type="file"
                        id="photos"
                        onChange={(e) => handleFileUpload(e, 'photos')}
                        accept=".jpg,.jpeg,.png"
                        multiple
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-ctc-blue hover:file:bg-blue-100"
                      />
                      {uploadedFiles.photos.length > 0 && (
                        <p className="mt-2 text-sm text-gray-600">
                          {uploadedFiles.photos.length} photo(s) selected
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    Notes/Context
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                    placeholder="Tell us anything about your system or problems you're seeing..."
                  />
                </div>

                <TurnstileWidget
                  ref={turnstile.ref}
                  onToken={turnstile.setToken}
                  action="chemistry_review"
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-ctc-blue text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 font-bold text-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      <FileText size={24} />
                      Get My Free Action Plan
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default ChemistryReviewPage;
